import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';

interface VerificationStatus {
  type: 'loading' | 'success' | 'error' | 'expired';
  message: string;
  details?: string;
}

const MarketingEmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasRequested = useRef(false);
  const [status, setStatus] = useState<VerificationStatus>({
    type: 'loading',
    message: 'Verifying your email subscription...',
  });

  useEffect(() => {
    if (hasRequested.current) return;
    hasRequested.current = true;

    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus({
          type: 'error',
          message: 'Invalid verification link',
          details: 'Token is missing from the link.',
        });
        return;
      }

      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/verifyEmailMarketing?token=${encodeURIComponent(token)}`
        );
        const result = await response.json();

        if (!response.ok) {
          if (response.status === 410) {
            setStatus({
              type: 'expired',
              message: 'This verification link has expired.',
              details: 'Please subscribe again from the original page.',
            });
            return;
          }

          setStatus({
            type: 'error',
            message: 'Verification failed',
            details: result.error || 'Unable to verify this subscription.',
          });
          return;
        }

        setStatus({
          type: 'success',
          message: 'Email verified. You are subscribed to RentSmart market updates.',
        });
      } catch (error) {
        setStatus({
          type: 'error',
          message: 'Verification failed',
          details: error instanceof Error ? error.message : 'Unknown verification error',
        });
      }
    };

    verifyEmail();
  }, [searchParams]);

  const renderBody = () => {
    if (status.type === 'loading') {
      return (
        <div className="text-center">
          <Loader2 className="w-14 h-14 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-semibold">{status.message}</p>
        </div>
      );
    }

    if (status.type === 'success') {
      return (
        <div className="text-center">
          <CheckCircle className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-900 mb-3">Subscription Confirmed</h1>
          <p className="text-slate-600 mb-6">{status.message}</p>
          <button
            onClick={() => navigate('/browse')}
            className="rounded-lg bg-emerald-600 text-white px-5 py-2.5 font-bold hover:bg-emerald-700"
          >
            Browse Properties
          </button>
        </div>
      );
    }

    if (status.type === 'expired') {
      return (
        <div className="text-center">
          <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-900 mb-3">Link Expired</h1>
          <p className="text-slate-600">{status.message}</p>
          {status.details ? <p className="text-sm text-slate-500 mt-2">{status.details}</p> : null}
          <button
            onClick={() => navigate('/explore-properties')}
            className="mt-6 rounded-lg bg-slate-900 text-white px-5 py-2.5 font-bold hover:bg-slate-800"
          >
            Return to Reports
          </button>
        </div>
      );
    }

    return (
      <div className="text-center">
        <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
        <h1 className="text-2xl font-black text-slate-900 mb-3">Unable to Verify</h1>
        <p className="text-slate-600">{status.message}</p>
        {status.details ? <p className="text-sm text-slate-500 mt-2">{status.details}</p> : null}
        <button
          onClick={() => navigate('/explore-properties')}
          className="mt-6 rounded-lg bg-slate-900 text-white px-5 py-2.5 font-bold hover:bg-slate-800"
        >
          Back to Reports
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-md mx-auto rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        {renderBody()}
      </div>
    </div>
  );
};

export default MarketingEmailVerification;
